import React, { useState, useEffect } from "react";
import {
  Loader,
  Table,
  Divider,
  Dropdown,
  Button,
  Icon,
  Message,
  Popup,
} from "semantic-ui-react";
import { Socialform, Form } from "../../../../api";
import { useAuth } from "../../../../hooks";
import {
  isAdmin,
  isMaster,
  isEditor,
  isReviewer,
} from "../../../../utils/checkPermission";
import "./ListSocialForms.scss";
import { formatDateView } from "../../../../utils/formatDate";
import { SocialForm } from "../SocialForm";
import { PERIODS, normalizeUnit } from "../../../../utils";
import { BasicDrawer, ConfirmModal } from "../../../Shared";
import {
  determineSocialUnits,
  socialCodes,
} from "../../../../utils/dataAndFormulas/socialForms";
import { useLanguage } from "../../../../contexts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UnitsForm } from "../UnitsForm";
import { UpdateUnitsModal } from "../UpdateUnitsModal";
const _ = require("lodash");

const socialFormController = new Socialform();
const formController = new Form();

export function ListSocialForms(props) {
  const { reload, onReload, siteSelected, yearSelected, configFormData, unitsRecorded } =
    props;
  const [socialforms, setSocialForms] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    user,
    accessToken,
  } = useAuth();

  const { translations } = useLanguage();

  const t = (key) => translations[key] || key;

  useEffect(() => {
    (async () => {
      if (yearSelected !== "" && siteSelected !== null && siteSelected !== "") {
        setLoading(true);
        try {
          const response =
            await socialFormController.getSocialFormsBySiteAndYear(
              accessToken,
              siteSelected,
              yearSelected
            );
          if (response.code === 200 && response.socialForms) {
            setSocialForms(response.socialForms);
          } else {
            setSocialForms([]);
          }
        } catch (error) {
          console.error(error);
          setSocialForms([]);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [yearSelected, siteSelected, reload]);

  if (loading) return <Loader active inline="centered" content="Cargando datos sociales..." />;

  return (
    <div className="list-social">
      <TablePeriods
        dataOriginal={socialforms}
        onReload={onReload}
        accessToken={accessToken}
        year={yearSelected}
        site={siteSelected}
        t={t}
        configFormData={configFormData}
        user={user}
        unitsRecorded={unitsRecorded}
      />

      <div className="list-social__pagination"></div>
      <Divider clearing />
    </div>
  );
}

function TablePeriods(props) {
  const { dataOriginal, onReload, accessToken, year, site, t, configFormData, user, unitsRecorded } =
    props;
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [dataDeleted, setDataDelete] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [uniqueFields, setUniqueFields] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Estados para edición batch
  const [editMode, setEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estado para expandir filas
  const [expandedFields, setExpandedFields] = useState({});

  // Estado para el modal de configuración de unidades
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showUnitsWarning, setShowUnitsWarning] = useState(false);

  // Estado para el modal de actualización masiva de unidades
  const [showUpdateUnitsModal, setShowUpdateUnitsModal] = useState(false);
  const [isApplyingUnits, setIsApplyingUnits] = useState(false);

  // Estado para el modal de advertencia de cambio de unidades (l → L)
  const [showLegacyUnitWarning, setShowLegacyUnitWarning] = useState(false);
  const [pendingUnitsToSave, setPendingUnitsToSave] = useState(null);

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);
  const onOpenCloseUnitsModal = () => setShowUnitsModal((prevState) => !prevState);

  const toggleExpand = (fieldName) => {
    setExpandedFields((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  useEffect(() => {
    (async () => {
      setUniqueFields(determineFields());
    })();
  }, [data, configFormData]);

  useEffect(() => {
    (async () => {
      if (dataOriginal) {
        setData(dataOriginal);
      }
    })();
  }, [dataOriginal]);

  useEffect(() => {
    if (!uniqueFields?.length) return;

    const values = {};
    uniqueFields.forEach((field) => {
      values[field] = unitsRecorded?.[field] || "";
    });

    setShowUnitsWarning(!configFormData && Object.keys(unitsRecorded || {}).length > 0);
    setSelectedUnits(values);
  }, [uniqueFields, unitsRecorded, configFormData]);

  // Alerta al salir con cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  // Atajos de teclado
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        if (hasChanges) {
          handleSaveChanges();
        }
      }
      if (e.key === "Escape" && editMode) {
        handleCancelEdit();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [editMode, hasChanges]);

  const validateUnitsConfigured = () => {
    if (!configFormData?.fields || configFormData.fields.length === 0) {
      return { valid: true };
    }

    const activeFields = configFormData.fields.filter(field => field.active !== false);

    if (activeFields.length === 0) {
      return { valid: false, message: t("no_active_fields") };
    }

    const fieldsWithUnit = activeFields.filter(field => field.unit && field.unit !== "");

    if (fieldsWithUnit.length === 0) {
      return {
        valid: false,
        message: t("units_not_configured") || "Debe configurar al menos una unidad antes de agregar un periodo. Haga clic en el ícono de tres puntos (⋯) en la columna de Unidades."
      };
    }

    return { valid: true };
  };

  const handleOpenModal = (period) => {
    setSelectedPeriod(data.find((item) => item.period === period));
    onOpenCloseModal();
  };

  const handleDeleteModal = (period) => {
    const form = data.find((item) => item.period === period);
    setConfirmContent(
      `${t("delete_dated_social_form")} ${formatDateView(form.date)}`
    );
    setDataDelete(form);
    onOpenCloseConfirm();
  };

  const openUpdateEffluentForm = (period) => {
    const form = data.find((item) => item.period === period);
    setModalContent(
      <SocialForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        socialForm={form}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />
    );
    setShowModal(true);
  };

  const openNewSocialForm = (period) => {
    const validation = validateUnitsConfigured();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    // console.log('🔍 ListSocialForms openNewSocialForm - unitsRecorded:', unitsRecorded);
    // console.log('🔍 ListSocialForms openNewSocialForm - configFormData:', configFormData);

    setModalContent(
      <SocialForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        period={period}
        year={year}
        siteSelected={site}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />
    );
    setShowModal(true);
  };

  const handleUnitsFormSave = (units) => {
    handleSaveUnits(units);
  };

  const handleSaveUnits = async (units) => {
    try {
      const formId = configFormData?._id;
      const currentFields = configFormData?.fields || [];

      const allSocialFieldsData = [
        { name: "days_worked", code: "Soc1" },
        { name: "normal_hours_worked", code: "Soc2" },
        { name: "fifty_percent_overtime", code: "Soc3" },
        { name: "one_hundred_percent_overtime", code: "Soc4" },
        { name: "female_production_workers", code: "Soc5" },
        { name: "male_production_workers", code: "Soc6" },
        { name: "other_gender_production_workers", code: "Soc7" },
        { name: "female_administrative_workers", code: "Soc8" },
        { name: "male_administrative_workers", code: "Soc9" },
        { name: "administrative_workers_other_genders", code: "Soc10" },
        { name: "female_temporary_workers", code: "Soc11" },
        { name: "male_temporary_workers", code: "Soc12" },
        { name: "temporary_workers_other_genders", code: "Soc13" },
        { name: "female_workers_leadership_positions", code: "Soc14" },
        { name: "male_workers_leadership_positions", code: "Soc15" },
        { name: "workers_under_cct", code: "Soc16" },
        { name: "work_accidents_with_sick_days", code: "Soc17" },
        { name: "in_itine_accidents", code: "Soc18" },
        { name: "first_aid_without_sick_days", code: "Soc19" },
        { name: "hours_training_social_issues", code: "Soc20" },
        { name: "hours_training_hys", code: "Soc21" },
        { name: "hours_environmental_awareness_training", code: "Soc22" },
        { name: "volunteer_hours", code: "Soc23" },
        { name: "complaints_or_suggestions_received", code: "Soc24" },
      ];

      if (!formId) {
        const fieldsToCreate = allSocialFieldsData.map((field) => ({
          code: field.code,
          name: field.name,
          active: true,
          unit: units[field.name] || "",
          standardized_coefficient: 1,
        }));

        const newFormData = {
          module: "social",
          site: site,
          fields: fieldsToCreate,
        };

        const response = await formController.createForm(accessToken, newFormData);

        if (response.code === 200 || response._id) {
          toast.success(t("units_updated_successfully") || "Unidades actualizadas correctamente");
          setShowUnitsWarning(false);
          onReload();
        } else {
          toast.error(t("error_updating_units") || "Error al actualizar las unidades");
        }
      } else if (currentFields.length === 0) {
        const fieldsToCreate = allSocialFieldsData.map((field) => ({
          code: field.code,
          name: field.name,
          active: true,
          unit: units[field.name] || "",
          standardized_coefficient: 1,
        }));

        const response = await formController.updateFormFields(accessToken, formId, fieldsToCreate);

        if (response.code === 200) {
          toast.success(t("units_updated_successfully") || "Unidades actualizadas correctamente");
          setShowUnitsWarning(false);
          onReload();
        } else {
          toast.error(t("error_updating_units") || "Error al actualizar las unidades");
        }
      } else {
        // Solo se envían los campos que cambian de unidad, de forma atómica
        // (ver updateFormFields en el controlador) — no se pisa el
        // documento entero, para no perder un toggle de 'active' guardado
        // desde Fuentes casi al mismo tiempo.
        const changes = currentFields
          .filter((field) => Object.prototype.hasOwnProperty.call(units, field.name))
          .map((field) => ({ name: field.name, unit: units[field.name] }));

        const response =
          changes.length > 0
            ? await formController.updateFormFields(accessToken, formId, changes)
            : { code: 200 };

        if (response.code === 200) {
          toast.success(t("units_updated_successfully") || "Unidades actualizadas correctamente");
          setShowUnitsWarning(false);
          onReload();
        } else {
          toast.error(t("error_updating_units") || "Error al actualizar las unidades");
        }
      }
    } catch (error) {
      console.error("❌ Social - Error al guardar unidades:", error);
      toast.error(t("error_updating_units") || "Error al actualizar las unidades");
    }
  };

  const handleApplyUnitsToForms = async (unitsToApply) => {
    try {
      setIsApplyingUnits(true);
      const response = await socialFormController.updateUnitsForYear(
        accessToken,
        site,
        year,
        unitsToApply
      );
      if (response.code === 200) {
        toast.success(
          `Se actualizaron las unidades en ${response.updatedCount} formularios de social`
        );
        setShowUpdateUnitsModal(false);
        onReload();
      } else {
        toast.error("Error al actualizar las unidades en formularios");
      }
    } catch (error) {
      console.error("Error al aplicar unidades:", error);
      toast.error("Error al actualizar las unidades en formularios");
    } finally {
      setIsApplyingUnits(false);
    }
  };

  const onDelete = async () => {
    try {
      const response = await socialFormController.deleteSocialForm(
        accessToken,
        dataDeleted._id
      );

      if (response.code === 200) {
        setIsSuccess(true);
        setModalMessage("Formulario eliminado correctamente!");
      } else {
        setIsSuccess(false);
        setModalMessage("Error al eliminar el formulario.");
      }

      setModalOpen(true);
      setDataDelete("");
      onOpenCloseConfirm();
      onReload();
    } catch (error) {
      console.error(error);
      setIsSuccess(false);
      setModalMessage("Error al eliminar el formulario.");
      setModalOpen(true);
    }
  };

  const periods = PERIODS;

  const determineFields = () => {
    const fields = [];

    if (
      configFormData &&
      configFormData.fields &&
      configFormData.fields.length > 0
    ) {
      configFormData.fields.forEach((field) => {
        if (field.active !== false) {
          fields.push(field.name);
        }
      });
    } else {
      Object.keys(socialCodes).forEach((key) => {
        fields.push(key);
      });
    }

    return fields;
  };

  const hasDataPeriod = (period) => {
    return data.some((entry) => entry.period === period);
  };

  const calculateTotalAndAverage = (field) => {
    const values = data.map((item) =>
      item[field]
        ? item[field].value && Number.isFinite(item[field].value)
          ? item[field].value
          : 0
        : 0
    );
    const totalFinal = values.reduce((acc, val) => acc + val, 0);
    const total = formatNumberWithThousands(totalFinal);
    const averageFinal = values.length ? totalFinal / values.length : 0;
    const average = formatNumberWithThousands(averageFinal);
    return { total, average };
  };

  const getUnitName = (field) => {
    const selectedUnitValue = normalizeUnit(selectedUnits[field]);

    if (!selectedUnitValue || selectedUnitValue === "") {
      return "- - -";
    }

    const units = determineSocialUnits(field);
    const unitObject = units.find((u) => u.value === selectedUnitValue);

    return unitObject ? unitObject.name : selectedUnitValue;
  };

  const handleUnitChange = (field, data) => {
    setSelectedUnits({ ...selectedUnits, [field]: data.value });
  };

  function formatNumberWithThousands(numero) {
    if (typeof numero !== "number" || isNaN(numero)) {
      return numero;
    }
    const entero = Math.round(numero);
    return entero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  function showWithTwoDecimals(numero) {
    if (typeof numero !== "number" || isNaN(numero)) {
      return numero;
    }

    if (numero % 1 === 0) {
      return numero.toString();
    } else {
      let numeroConCuatroDecimales = numero.toFixed(2);
      return parseFloat(numeroConCuatroDecimales).toString();
    }
  }

  const handleCellChange = (field, period, item, newValue) => {
    const key = `${period}_${field}`;
    const originalValue = item?.[field]?.value || null;

    if (newValue === originalValue) {
      const updatedEdits = { ...editedValues };
      delete updatedEdits[key];
      setEditedValues(updatedEdits);
      setHasChanges(Object.keys(updatedEdits).length > 0);
      return;
    }

    setEditedValues({
      ...editedValues,
      [key]: {
        formId: item?._id || null,
        period: period,
        field: field,
        value: newValue,
        unit: selectedUnits[field],
        originalValue: originalValue,
        isNew: !item || !item._id,
        year: year,
        site: site,
      },
    });
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!hasChanges) return;

    try {
      setIsSaving(true);

      const updates = [];
      const creates = [];

      Object.values(editedValues).forEach((change) => {
        if (change.isNew) {
          creates.push({
            period: change.period,
            year: change.year,
            site: change.site,
            field: change.field,
            value: change.value,
            unit: change.unit,
            code: socialCodes[change.field],
          });
        } else {
          updates.push({
            formId: change.formId,
            field: change.field,
            value: change.value,
            unit: change.unit,
            code: socialCodes[change.field],
          });
        }
      });

      const response = await socialFormController.batchUpdateSocialForms(
        accessToken,
        {
          updates,
          creates,
          site,
          year,
          creatorUser: user._id,
        }
      );

      if (response.code === 200) {
        toast.success(
          `✅ ${response.updated} formularios actualizados, ${response.created} creados`
        );
        setEditedValues({});
        setHasChanges(false);
        setEditMode(false);
        onReload();
      } else {
        toast.error("❌ Error al guardar los cambios");
      }
    } catch (error) {
      console.error("Error saving batch changes:", error);
      toast.error("❌ Error al guardar los cambios");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedValues({});
    setHasChanges(false);
  };

  const hasLoadedData = data && data.length > 0;

  return (
    <>
      {/* Botones de control batch edit */}
      {(isMaster(user.role) || isAdmin(user.role) || isEditor(user.role)) && (
        <div className="batch-actions">
          {!editMode ? (

            <Popup
              content={t("enable_multiple_edit")}
              trigger={
                     <Button
              className="batch-btn primary"
              icon
              //labelPosition="left"
              onClick={() => setEditMode(true)}
              disabled={!hasLoadedData}
            >
              <Icon name="edit" />
              {/* Editar Múltiples Períodos */}
            </Button> }
            />

          ) : (
            <>
              <Button
                className="batch-btn success"
                icon
                labelPosition="left"
                onClick={handleSaveChanges}
                disabled={!hasChanges || isSaving}
                loading={isSaving}
              >
                <Icon name="save" />
                Guardar Cambios
                {hasChanges && ` (${Object.keys(editedValues).length})`}
              </Button>

              <Button
                className="batch-btn cancel"
                icon
                labelPosition="left"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                <Icon name="cancel" />
                Cancelar
              </Button>

              {hasChanges && (
                <span style={{ color: "#f57c00", fontWeight: "bold" }}>
                  <Icon name="warning sign" />
                  {Object.keys(editedValues).length} cambio(s) pendiente(s)
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Mensaje de cambios pendientes */}
      {hasChanges && (
        <Message warning>
          <Message.Header>Cambios sin guardar</Message.Header>
          <p>
            Tienes {Object.keys(editedValues).length} cambio(s) pendiente(s). No
            olvides guardar antes de salir. (Ctrl+S para guardar, Esc para
            cancelar)
          </p>
        </Message>
      )}

      {/* Advertencia de unidades tomadas de formularios */}
      {showUnitsWarning && (
        <Message info>
          <Message.Header>Configuración de unidades recomendada</Message.Header>
          <p>
            Las unidades mostradas provienen de formularios guardados. Para una mejor consistencia,
            se recomienda configurar las unidades explícitamente haciendo clic en el ícono de tres puntos (⋯)
            en la columna "Unidades".
          </p>
        </Message>
      )}

      <div className="list-social__table-wrapper">
        <Table className="social-table" unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="sticky-col-1 pill-left-start">
                {t("code")}
              </Table.HeaderCell>
              <Table.HeaderCell className="sticky-col-2">
                {t("concept")}
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-left-end">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                  {t("units")}
                  <Dropdown
                    icon="ellipsis vertical"
                    floating
                    className="icon"
                    style={{ cursor: "pointer" }}
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        text={t("configure_units") || "Configurar unidades"}
                        icon="setting"
                        onClick={onOpenCloseUnitsModal}
                      />
                      <Dropdown.Item
                        text={t("update_units_in_forms") || "Modificar unidades en formularios"}
                        icon="edit"
                        onClick={() => setShowUpdateUnitsModal(true)}
                      />
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-gap" />
              <Table.HeaderCell colSpan="3" textAlign="center" className="pill-right">
                {t("REPORTING_PERIOD")} {year}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {uniqueFields.map((field) => {
              const isExpanded = expandedFields[field];
              const { total, average } = calculateTotalAndAverage(field);
              const hasMismatchedUnit = data.some((item) => {
                const itemUnit = normalizeUnit(item?.[field]?.unit);
                const configuredUnit = normalizeUnit(selectedUnits[field]);
                return itemUnit && configuredUnit && itemUnit !== configuredUnit;
              });

              return (
                <React.Fragment key={field}>
                  {/* Fila resumen */}
                  <Table.Row>
                    <Table.Cell className="sticky-col-1 pill-left-start">
                      {socialCodes[field]}
                    </Table.Cell>
                    <Table.Cell className="sticky-col-2">
                      {t(field)}
                    </Table.Cell>
                    <Table.Cell textAlign="center" className="pill-left-end">
                      {getUnitName(field)}
                    </Table.Cell>
                    <Table.Cell className="pill-gap" />
                    <Table.Cell colSpan="3" className="pill-right">
                      <div className="summary-flex">
                        <span className="summary-item">
                          {t("total")}: <strong>{total}</strong>
                        </span>
                        <span className="summary-item">
                          {t("average")}: <strong>{average}</strong>
                        </span>
                        <Button
                          icon
                          size="tiny"
                          className="expand-btn"
                          onClick={() => toggleExpand(field)}
                        >
                          <Icon name={isExpanded ? "chevron up" : "chevron down"} />
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>

                  {/* Fila cabecera de meses (expandida) */}
                  {isExpanded && (
                    <Table.Row className="months-header-row">
                      <Table.Cell colSpan="7" style={{ padding: 0 }} className="months-full-row">
                        <div className="months-flex-container">
                          {periods.map((period) => (
                            <div key={period} className="month-flex-cell month-flex-header">
                              {t(period).slice(0, 3)}
                              {!editMode && (
                                hasDataPeriod(period) ? (
                                  <Dropdown
                                    inline
                                    size="tiny"
                                    icon="ellipsis vertical"
                                    floating
                                    className="icon"
                                  >
                                    <Dropdown.Menu>
                                      <Dropdown.Item
                                        text={t("edit")}
                                        icon={<Icon name="edit" style={{ color: "gray" }} />}
                                        onClick={() => openUpdateEffluentForm(period)}
                                      />
                                      <Dropdown.Item
                                        text={t("delete")}
                                        icon={<Icon name="trash" style={{ color: "red" }} />}
                                        onClick={() => handleDeleteModal(period)}
                                      />
                                    </Dropdown.Menu>
                                  </Dropdown>
                                ) : (
                                  <Dropdown
                                    link
                                    size="tiny"
                                    icon="ellipsis vertical"
                                    floating
                                    className="icon"
                                  >
                                    <Dropdown.Menu>
                                      {(isAdmin(user.role) || isEditor(user.role) || isMaster(user.role)) ? (
                                        <Dropdown.Item
                                          text={t("load_data")}
                                          icon="plus"
                                          onClick={() => openNewSocialForm(period)}
                                        />
                                      ) : null}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}

                  {/* Fila datos de meses (expandida) */}
                  {isExpanded && (
                    <Table.Row className="months-data-row">
                      <Table.Cell colSpan="7" style={{ padding: 0 }} className="months-full-row">
                        <div className="months-flex-container">
                          {periods.map((period) => {
                            const item = data.find((d) => d.period === period);
                            const editKey = `${period}_${field}`;
                            const isEdited = editedValues[editKey] !== undefined;
                            const hasUnit = selectedUnits[field] && selectedUnits[field] !== "";
                            let cellContent;

                            if (editMode) {
                              const displayVal = isEdited
                                ? editedValues[editKey].value
                                : (item?.[field]?.value ?? "");
                              cellContent = (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={displayVal}
                                  onChange={(e) =>
                                    handleCellChange(
                                      field,
                                      period,
                                      item,
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  disabled={!hasUnit || isReviewer(user.role)}
                                  style={{
                                    width: "100%",
                                    height: "30px",
                                    border: isEdited ? "2px solid #fbc02d" : "1px solid #ccc",
                                    borderRadius: "4px",
                                    padding: "3px 6px",
                                    textAlign: "right",
                                    fontSize: "11px",
                                    background: hasUnit ? "white" : "#f5f5f5",
                                    color: !hasUnit ? "#999" : isReviewer(user.role) ? "#000" : "inherit",
                                    MozAppearance: "textfield",
                                    appearance: "textfield",
                                  }}
                                  placeholder="-"
                                />
                              );
                            } else {
                              cellContent =
                                item?.[field]?.value !== undefined
                                  ? formatNumberWithThousands(item[field].value)
                                  : "-";
                            }

                            return (
                              <div key={period} className="month-flex-cell month-flex-data">
                                {cellContent}
                              </div>
                            );
                          })}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={1200}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <BasicDrawer
        show={showModal}
        close={onOpenCloseModal}
      >
        {modalContent}
      </BasicDrawer>

      <ConfirmModal
        open={showConfirm}
        title={t("delete")}
        content={confirmContent}
        onConfirm={onDelete}
        onCancel={onOpenCloseConfirm}
        danger
      />

      <ConfirmModal
        open={modalOpen}
        icon={<Icon name={isSuccess ? "check circle outline" : "warning sign"} color={isSuccess ? "green" : "red"} />}
        title={isSuccess ? "Éxito" : "Error"}
        content={modalMessage}
        onCancel={() => setModalOpen(false)}
        cancelText="Cerrar"
      />

      <UnitsForm
        show={showUnitsModal}
        onClose={onOpenCloseUnitsModal}
        onSave={handleUnitsFormSave}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
      />

      <UpdateUnitsModal
        show={showUpdateUnitsModal}
        onClose={() => setShowUpdateUnitsModal(false)}
        onApply={handleApplyUnitsToForms}
        currentUnits={selectedUnits}
        registeredFields={configFormData?.fields || []}
        isLoading={isApplyingUnits}
      />

      <ConfirmModal
        open={showLegacyUnitWarning}
        icon={<Icon name="warning sign" color="orange" />}
        title="Cambio de unidades"
        content={
          <>
            <p>
              Los registros anteriores continuarán guardados con la unidad{" "}
              <strong>"l"</strong> (litros). A partir de ahora, los nuevos
              datos se guardarán con <strong>"L"</strong> (Litros).
            </p>
            <p>¿Desea continuar con el cambio?</p>
          </>
        }
        onCancel={() => setShowLegacyUnitWarning(false)}
        onConfirm={() => {
          setShowLegacyUnitWarning(false);
          handleSaveUnits(pendingUnitsToSave);
        }}
        cancelText="Cancelar"
        confirmText="Confirmar"
      />
    </>
  );
}
